import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function SendSmsWidget() {
  const { user } = useAuth();
  const defaultMsg = `Purely IT Temp Monitor — test alert from ${user?.name || "admin"}`;
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState(defaultMsg);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  async function send(dryRun) {
    if (!phone.trim()) {
      toast.error("Phone number required");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const body = { phone: phone.trim(), message: message.trim() || defaultMsg };
      if (dryRun) body.key_override = "textbelt_test";
      const { data } = await api.post("/test/sms", body);
      setResult({ ...data, mode: dryRun ? "dry-run" : "live" });
      if (data.success) {
        toast.success(
          dryRun
            ? "Dry-run OK (no SMS sent)"
            : `Sent! Quota remaining: ${data.quotaRemaining}`,
        );
      } else {
        toast.error(data.error || "Send failed");
      }
    } catch (err) {
      const msg = formatApiError(err);
      toast.error(msg);
      setResult({ success: false, error: msg, mode: dryRun ? "dry-run" : "live" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="border border-zinc-800 bg-zinc-900/40 p-6"
      data-testid="send-sms-widget"
    >
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
          Admin tool
        </div>
        <div className="font-display text-xl font-bold uppercase tracking-tight">
          Send test SMS
        </div>
        <p className="text-xs text-zinc-400 mt-1">
          Live mode uses your TextBelt quota. Use dry-run to verify the code path
          without burning credits.
        </p>
      </div>

      <div className="space-y-3">
        <div>
          <Label htmlFor="sms-phone" className="text-xs uppercase tracking-widest text-zinc-400">
            Phone
          </Label>
          <Input
            id="sms-phone"
            data-testid="sms-phone-input"
            placeholder="+15551234567 or 5551234567"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm"
          />
        </div>
        <div>
          <Label htmlFor="sms-msg" className="text-xs uppercase tracking-widest text-zinc-400">
            Message
          </Label>
          <Input
            id="sms-msg"
            data-testid="sms-message-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm"
          />
        </div>
        <div className="flex gap-2 pt-1">
          <Button
            type="button"
            disabled={busy}
            onClick={() => send(true)}
            data-testid="sms-dry-run-button"
            className="rounded-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-100 uppercase tracking-widest text-xs flex-1"
          >
            Dry-run (test key)
          </Button>
          <Button
            type="button"
            disabled={busy}
            onClick={() => send(false)}
            data-testid="sms-send-live-button"
            className="rounded-sm bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold flex-1"
          >
            Send live
          </Button>
        </div>
      </div>

      {result && (
        <pre
          data-testid="sms-result"
          className="mt-4 bg-zinc-950 border border-zinc-800 text-[11px] text-zinc-300 p-3 font-mono overflow-auto"
        >
          {JSON.stringify(result, null, 2)}
        </pre>
      )}
    </div>
  );
}
